// The Boss Ladder, on the Trail's boss stops (the Bosses page with the Trail off): an endless climb above the three
// bosses (shared/ladder.ts). The left column lists each step by boss and level (a Trail stop: its boss's only); the right shows the chosen one: what it
// takes to enter, the drops with their chances, a Power estimate for your team (never exact odds), and the entry with
// an optional auto-repeat.
// Rules and formulas live on the Help page.
import { BOSSES, SPECIES } from '../shared/species.ts';
import { ITEM } from '../shared/items.ts';
import { LADDER_MAX_COMMIT, LADDER_REPEAT_MS, crestItem, highestOpenStep, ladderCoins, ladderName, stepBoss, stepGate, stepKey, stepLevel, stepOpen, stepRewards } from '../shared/ladder.ts';
import { VERDICT_NAME, bossEstimate, bossWeakTo, typeEdge } from '../shared/boss-estimate.ts';
import type { ClientMsg, CreatureDTO } from '../shared/protocol.ts';
import { S, serverNow } from './state.ts';
import { spriteUrl, iconUrl, bossPic } from './assets.ts';
import { esc, full, num, elChip, cname } from './fmt.ts';
import { openDialog } from './dialog.ts';
import { heldPiece, heldPool, pieceChance } from '../shared/held.ts';
import { bossNode, cleared } from '../shared/trail.ts';
import { bossBoardHtml, bossBattleOf, teamOf } from './bossboard.ts';
import { afterReplay } from './boardview.ts';

let picked = 0;
let commit = 5;
let stopOnLoss = true;

const coin = (n: number) => `<span class="coinv"><img src="${iconUrl('coin')}" alt="coins">${full(n)}</span>`;
const odds = (p: number) => `${(Math.round(p * 1000) / 10).toLocaleString()}%`;

/**
 * The estimate card, shared with the three bosses: the verdict, both Powers, and the team in a row with each
 * creature's type and level edge marked, so a player can see what to swap without being told the outcome.
 */
export function estimateCard(team: CreatureDTO[], bossId: string, level: number): string {
  const name = esc(SPECIES[bossId].name);
  const weak = bossWeakTo(bossId);
  const weakChips = weak.length ? weak.map(elChip).join('') : '<span class="muted">nothing</span>';
  if (!team.length) return `<div class="est"><div class="est-top"><span class="verdict outmatched">No team</span></div><div class="est-weak"><span class="muted">Weak to</span>${weakChips}</div></div>`;
  const e = bossEstimate(team, bossId, level, S.trainer?.halls);
  const mates = team.map((c) => {
    const te = typeEdge(c.species, bossId);
    const lv = c.level / level;
    const marks = [
      te.off > 1 ? '<i class="up" title="Hits it hard">▲</i>' : '',
      te.def > 1 ? '<i class="dn" title="Takes hard hits from it">▼</i>' : '',
    ].join('');
    const lvCls = lv >= 1 ? 'hi' : lv >= 0.9 ? 'mid' : 'lo';
    return `<span class="mate" title="${esc(cname(c))} · Lv ${full(c.level)}"><img src="${spriteUrl(c.species, c.rolls.aura)}" alt="">${marks ? `<span class="mk">${marks}</span>` : ''}<span class="mlv ${lvCls}">${full(c.level)}</span></span>`;
  }).join('');
  return `<div class="est">
    <div class="est-top"><span class="verdict ${e.verdict}">${VERDICT_NAME[e.verdict]}</span>
      <span class="est-pow"><span class="muted">Your Power</span> <b>${full(e.team)}</b> <span class="muted">vs</span> <b>${full(e.boss)}</b></span></div>
    <div class="est-team">${mates}</div>
    <div class="est-weak"><span class="muted">${name} is weak to</span>${weakChips}</div></div>`;
}

/** On a Trail boss stop only that boss's steps show (`boss`); the Bosses page lists them all. */
const ofBoss = (r: number, boss?: string) => !boss || stepBoss(r).id === boss;

function stepList(top: number, level: number, boss?: string) {
  const k = boss ? BOSSES.length : 1;
  const last = Math.max(top + 4 * k, 8 * k);
  const best = S.trainer?.ladder?.best ?? 0;
  const rows: string[] = [];
  for (let r = 1; r <= last; r++) {
    if (!ofBoss(r, boss)) continue;
    const b = stepBoss(r), open = stepOpen(r, level);
    rows.push(`<button class="lstep${r === picked ? ' on' : ''}${open ? '' : ' locked'}${r <= best ? ' cleared' : ''}" data-act="ladder-pick" data-id="${r}"${r === picked ? ' aria-current="true"' : ''}>
      <img src="${spriteUrl(b.id)}" alt=""><b class="nm">${esc(b.name)}</b><span class="lv">Lv ${full(stepLevel(r))}</span>${r <= best ? '<span class="ok" title="Cleared">✓</span>' : ''}</button>`);
  }
  return `<div class="lsteps" role="list">${rows.join('')}</div>`;
}

/**
 * The held-item piece drop on a boss card or a Boss Ladder step, while wins drop one (server/trail.ts piecesOpen):
 * held items on, and the boss's Trail first clear done (or the Trail off).
 */
export function pieceDrop(boss: string, from: 'boss' | 'ladder'): string {
  const pool = heldPool(boss), node = bossNode(boss);
  if (!S.heldOn || !pool.length || (node && S.trailOn && !cleared(S.trainer?.trail, node.id))) return '';
  return `<div class="drop rare" tabindex="0" data-tip="${esc(`A piece of one of ${SPECIES[boss]?.name ?? boss}'s held items. Ten pieces of one item forge it.`)}"><img src="${iconUrl(heldPiece(pool[0].id))}" alt=""><span class="pct">${odds(pieceChance(from))}</span><b>Held-item piece</b></div>`;
}

function drops(r: number) {
  const t = S.trainer!;
  const cards = [`<div class="drop" tabindex="0" data-tip="${full(ladderCoins(r))} coins"><img src="${iconUrl('coin')}" alt=""><span class="pct">${num(ladderCoins(r))}</span><b>Coins</b></div>`];
  for (const x of stepRewards(r)) {
    const it = ITEM[x.item];
    const qty = x.qty[0] === x.qty[1] ? '' : ` ×${x.qty[0]}-${x.qty[1]}`;
    cards.push(`<div class="drop${x.chance < 1 ? ' rare' : ''}" tabindex="0" data-tip="${esc(`${it.desc}\nYou have ${full(t.bag[x.item] ?? 0)}.`)}"><img src="${iconUrl(x.item)}" alt=""><span class="pct">${x.chance >= 1 ? '100%' : odds(x.chance)}</span><b>${esc(it.name)}${qty}</b></div>`);
  }
  cards.push(pieceDrop(stepBoss(r).id, 'ladder'));
  return `<div class="drops">${cards.join('')}</div>`;
}

function entry(r: number, team: CreatureDTO[]) {
  const t = S.trainer!;
  const key = stepKey(r), have = t.bag[key] ?? 0, gate = stepGate(r);
  const open = t.level >= gate;
  const run = t.ladderRun;
  const why = !open ? `Opens at Lv ${full(gate)}` : !have ? `Needs a ${ITEM[key].name}` : !team.length ? 'No team' : '';
  const keyChip = `<span class="lkey"><img src="${iconUrl(key)}" alt="">${esc(ITEM[key].name)} <b>${full(have)}</b></span>`;
  if (run) {
    const next = Math.max(0, Math.ceil((run.next - serverNow()) / 1000));
    return `<div class="lentry running">${keyChip}<span class="grow"><b>Auto-repeat · ${esc(ladderName(run.step))}</b><span class="muted">${run.won} of ${run.fought} won · ${run.left} ${run.left === 1 ? 'key' : 'keys'} left · next in ${next} s</span></span>
      <button class="btn" data-act="ladder-stop">Stop</button></div>`;
  }
  const most = Math.min(have, LADDER_MAX_COMMIT);
  const n = Math.max(2, Math.min(commit, Math.max(2, most)));
  return `<div class="lentry">${keyChip}
      <button class="btn primary" data-act="ladder-fight" data-id="${r}"${why ? ' disabled' : ''}>${why ? esc(why) : `Fight ${esc(stepBoss(r).name)}`}</button>
      <span class="lrepeat"><label>Repeat × <input type="number" min="2" max="${Math.max(2, most)}" value="${n}" data-ladder-commit aria-label="Keys to commit"></label>
        <label class="lstop"><input type="checkbox" data-ladder-stoploss${stopOnLoss ? ' checked' : ''}> Stop on a loss</label>
        <button class="btn" data-act="ladder-auto" data-id="${r}"${why || most < 2 ? ' disabled' : ''}>Start</button></span></div>`;
}

function lastResult() {
  const b = S.ladder;
  if (!b) return '';
  const boss = SPECIES[b.boss];
  const dealt = b.log.filter((x) => x.by === 'team').reduce((a, x) => a + x.dmg, 0);
  const share = Math.min(100, Math.round((dealt / b.bossMaxHp) * 100));
  const items = b.rewards.items.map((i) => `<span class="rw"><img src="${iconUrl(i.item)}" alt="">${esc(ITEM[i.item].name)} ×${i.qty}</span>`).join('');
  const replay = bossBoardHtml({ boss: b.boss, level: b.level, win: b.win, log: b.log, bossMaxHp: b.bossMaxHp, teamMaxHp: b.teamMaxHp, team: teamOf(b) }, b);
  return `${replay ? `<section class="panel boss-replay">${replay}</section>` : ''}${afterReplay(bossBattleOf(b), `<section class="panel bossres ${b.win ? 'win' : 'loss'}"><img src="${spriteUrl(b.boss)}" alt=""><div class="grow">
    <h3>${esc(boss.name)} · Lv ${full(b.level)} ${b.win ? 'defeated' : `held on · your team dealt ${share}% of its HP`}</h3>
    ${b.win ? `<div class="rewards">${coin(b.rewards.coins)}${items}</div>` : ''}
    ${b.stopped ? `<p class="muted">Auto-repeat finished: ${esc(b.stopped)}.</p>` : ''}</div></section>`)}`;
}

/** The step shown first: the running climb's, else the highest open one (of `boss`'s steps on its Trail stop). */
function firstPick(top: number, boss?: string): number {
  const run = S.trainer?.ladderRun?.step;
  if (run && ofBoss(run, boss)) return run;
  for (let r = Math.max(1, top); r >= 1; r--) if (ofBoss(r, boss)) return r;
  return BOSSES.findIndex((b) => b.id === boss) + 1;
}

export function ladderSection(boss?: string) {
  const t = S.trainer!;
  const team = t.team.map((id) => S.creatures.get(id)).filter((c): c is CreatureDTO => !!c);
  const top = highestOpenStep(t.level);
  if (!picked || !ofBoss(picked, boss)) picked = firstPick(top, boss);
  const r = picked, b = stepBoss(r), gate = stepGate(r), open = t.level >= gate, level = stepLevel(r);
  const crests = (['fenrir', 'typhon', 'hachimata'] as const).filter((id) => (t.bag[crestItem(id)] ?? 0) > 0);
  const rec = t.ladder;
  const month = rec && rec.month === new Date(serverNow()).toISOString().slice(0, 7) ? rec.monthBest : 0;
  return `<div class="sech ladder-h"><h3>Boss Ladder</h3>${rec?.best ? `<span class="muted">Best ${esc(ladderName(rec.best))}${month ? ` · this month ${esc(ladderName(month))}` : ''}</span>` : ''}<span class="spacer"></span><a class="link" href="#/help/ladder">How the ladder works</a></div>
    ${lastResult()}
    <div class="ladder">${stepList(top, t.level, boss)}
      <section class="panel lstep-detail"><div class="lhead">${bossPic(b.id)}<div class="grow">
        <h3>${esc(b.name)} <span class="lvl">Lv ${full(level)}</span></h3>
        <div class="chips">${b.elements.map(elChip).join('')}<span class="chip ${open ? 'good' : 'bad'}">Trainer Lv ${full(gate)}+</span></div>
      </div></div>
      ${estimateCard(team, b.id, level)}
      <h4 class="sec">Drops <span class="muted">per win</span></h4>
      ${drops(r)}
      ${entry(r, team)}
      ${crests.length ? `<p class="muted crests">Your crests: ${crests.map((id) => `<span class="rw"><img src="${iconUrl(crestItem(id))}" alt="">${esc(ITEM[crestItem(id)].name)}</span>`).join(' ')}</p>` : ''}
      </section></div>`;
}

export function ladderClick(el: HTMLElement, send: (m: ClientMsg) => void, rerender: () => void): boolean {
  const r = Number(el.dataset.id);
  switch (el.dataset.act) {
    case 'ladder-pick': picked = r; rerender(); return true;
    case 'ladder-stop': send({ t: 'ladderStop' }); return true;
    case 'ladder-fight': {
      const t = S.trainer!, key = ITEM[stepKey(r)].name;
      void openDialog({
        title: `Fight ${ladderName(r)}?`,
        body: `<p>${esc(key)}: ${full(t.bag[stepKey(r)] ?? 0)}</p>`,
        actions: [{ label: `Fight (uses 1 ${key})`, value: 'go', kind: 'primary' }],
      }).then((x) => { if (x.value) send({ t: 'ladder', step: r }); });
      return true;
    }
    case 'ladder-auto': {
      const t = S.trainer!, key = ITEM[stepKey(r)].name;
      const n = Math.max(2, Math.min(commit, LADDER_MAX_COMMIT, t.bag[stepKey(r)] ?? 0));
      void openDialog({
        title: `Auto-repeat ${ladderName(r)}?`,
        body: `<p><b>${n} ${esc(key)}s</b> · every ${LADDER_REPEAT_MS / 1000} s${stopOnLoss ? ' · stops on a loss' : ''}</p>`,
        actions: [{ label: `Commit ${n} keys`, value: 'go', kind: 'primary' }],
      }).then((x) => { if (x.value) send({ t: 'ladder', step: r, repeat: n, stopOnLoss }); });
      return true;
    }
  }
  return false;
}

export function ladderChange(el: HTMLInputElement | HTMLSelectElement): boolean {
  if (el.dataset.ladderCommit !== undefined) { commit = Math.max(2, Math.min(LADDER_MAX_COMMIT, Math.floor(Number(el.value)) || 2)); return true; }
  if (el.dataset.ladderStoploss !== undefined) { stopOnLoss = (el as HTMLInputElement).checked; return true; }
  return false;
}
